import React, { useCallback, useState, useMemo } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useNavigate } from 'react-router-dom';
import { EMZI_COLORS, EMZI_RADIUS, EMZI_SPACING, getButtonStyle } from '../styles/emziCI';
import CampaignNode from '../components/graph/CampaignNode';
import ProductNode from '../components/graph/ProductNode';
import TeamNode from '../components/graph/TeamNode';

// Custom node types
const nodeTypes = {
  campaign: CampaignNode,
  product: ProductNode,
  team: TeamNode,
};

// Sample data - EMZI Care campaigns with products and packs
const initialNodes = [
  // Campaign: Ramadan Wellness Bundle
  {
    id: 'campaign-1',
    type: 'campaign',
    position: { x: 400, y: 50 },
    data: {
      name: 'Ramadan Wellness Bundle 2025',
      description: 'Exclusive bundle promoting health during Ramadan',
      startDate: '2025-03-01',
      endDate: '2025-04-01',
      linkedPacks: [
        { pack_id: 'promo-1', pack_type: 'promotion', name: { en: 'Buy 2 Get 1 Free' } },
        { pack_id: 'val-1', pack_type: 'values', name: { en: 'Ramadan Values' } },
        { pack_id: 'amb-1', pack_type: 'ambassador', name: { en: 'Ustaz Kazim' } },
      ],
    },
  },

  // Products under Campaign 1
  {
    id: 'product-1',
    type: 'product',
    position: { x: 200, y: 300 },
    data: {
      name: 'Kopi Ala Kazim',
      description: 'Herbal coffee for glucose support',
      price: 'RM 89.90',
      linkedPacks: [
        { pack_id: 'prod-1', pack_type: 'product', name: { en: 'Kopi Ala Kazim Pack' } },
        { pack_id: 'health-1', pack_type: 'health', name: { en: 'Glucose Support Claims' } },
        { pack_id: 'comp-1', pack_type: 'compliance', name: { en: 'Health Product Compliance' } },
      ],
    },
  },

  {
    id: 'product-2',
    type: 'product',
    position: { x: 600, y: 300 },
    data: {
      name: 'Olivetin',
      description: 'Heart & cholesterol support',
      price: 'RM 89.90',
      linkedPacks: [
        { pack_id: 'prod-2', pack_type: 'product', name: { en: 'Olivetin Pack' } },
        { pack_id: 'health-2', pack_type: 'health', name: { en: 'Cardiovascular Claims' } },
        { pack_id: 'comp-1', pack_type: 'compliance', name: { en: 'Health Product Compliance' } },
      ],
    },
  },

  // Campaign: Heart Health Awareness
  {
    id: 'campaign-2',
    type: 'campaign',
    position: { x: 1000, y: 50 },
    data: {
      name: 'Heart Health Awareness Month',
      description: 'Promoting cardiovascular wellness',
      startDate: '2025-02-01',
      endDate: '2025-02-28',
      linkedPacks: [
        { pack_id: 'dir-1', pack_type: 'direction', name: { en: 'Heart Health Strategy' } },
        { pack_id: 'val-2', pack_type: 'values', name: { en: 'Wellness First Values' } },
      ],
    },
  },

  // Product under Campaign 2
  {
    id: 'product-3',
    type: 'product',
    position: { x: 1000, y: 300 },
    data: {
      name: 'Olivetin',
      description: 'Featured in Heart Health campaign',
      price: 'RM 89.90',
      linkedPacks: [
        { pack_id: 'prod-2', pack_type: 'product', name: { en: 'Olivetin Pack' } },
        { pack_id: 'health-2', pack_type: 'health', name: { en: 'Cardiovascular Claims' } },
      ],
    },
  },

  // Teams
  {
    id: 'team-1',
    type: 'team',
    position: { x: 400, y: 550 },
    data: {
      name: 'Marketing Team',
      memberCount: 8,
    },
  },

  {
    id: 'team-2',
    type: 'team',
    position: { x: 1000, y: 550 },
    data: {
      name: 'Sales Team - West',
      memberCount: 12,
    },
  },

  // Service Node
  {
    id: 'service-1',
    type: 'product',
    position: { x: 50, y: 300 },
    data: {
      name: 'EMZI Care Farmasi',
      description: 'Pharmacy consultation service',
      price: 'Free',
      linkedPacks: [
        { pack_id: 'serv-1', pack_type: 'services', name: { en: 'Farmasi Service Pack' } },
        { pack_id: 'comp-2', pack_type: 'compliance', name: { en: 'Pharmacy Compliance' } },
      ],
    },
  },
];

const initialEdges = [
  // Campaign 1 → Products
  { id: 'e1-1', source: 'campaign-1', target: 'product-1', type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed, color: EMZI_COLORS.warning } },
  { id: 'e1-2', source: 'campaign-1', target: 'product-2', type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed, color: EMZI_COLORS.warning } },
  { id: 'e1-3', source: 'campaign-1', target: 'service-1', type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed, color: EMZI_COLORS.warning }, animated: true },
  
  // Campaign 2 → Product
  { id: 'e2-1', source: 'campaign-2', target: 'product-3', type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed, color: EMZI_COLORS.warning } },
  
  // Products → Teams
  { id: 'e3-1', source: 'product-1', target: 'team-1', type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed, color: EMZI_COLORS.secondary } },
  { id: 'e3-2', source: 'product-2', target: 'team-1', type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed, color: EMZI_COLORS.secondary } },
  { id: 'e3-3', source: 'product-3', target: 'team-2', type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed, color: EMZI_COLORS.secondary } },
];

const CampaignGraphView = () => {
  const navigate = useNavigate();
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState(null);

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
  }, []);

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: EMZI_COLORS.background }}>
      {/* Header */}
      <div
        style={{
          padding: EMZI_SPACING.md,
          backgroundColor: '#FFFFFF',
          borderBottom: `1px solid ${EMZI_COLORS.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: EMZI_COLORS.textPrimary }}>
            📊 Campaign Graph View
          </h1>
          <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: EMZI_COLORS.textSecondary }}>
            Visualize campaigns, products, and their linked Knowledge Packs
          </p>
        </div>
        <button
          onClick={() => navigate('/knowledge-packs')}
          style={{
            ...getButtonStyle('outlined'),
            padding: '8px 16px',
            fontSize: '13px',
          }}
        >
          ← Back to Library
        </button>
      </div>

      {/* Graph Container */}
      <div style={{ flex: 1, position: 'relative' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          nodeTypes={nodeTypes}
          fitView
          minZoom={0.5}
          maxZoom={1.5}
          defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
        >
          <Background color={EMZI_COLORS.border} gap={16} />
          <Controls />
          <MiniMap
            nodeColor={(node) => {
              switch (node.type) {
                case 'campaign':
                  return EMZI_COLORS.warning;
                case 'product':
                  return EMZI_COLORS.primary;
                case 'team':
                  return EMZI_COLORS.secondary;
                default:
                  return EMZI_COLORS.border;
              }
            }}
            maskColor="rgba(0, 0, 0, 0.1)"
          />
        </ReactFlow>

        {/* Legend */}
        <div
          style={{
            position: 'absolute',
            top: EMZI_SPACING.md,
            right: EMZI_SPACING.md,
            backgroundColor: '#FFFFFF',
            padding: EMZI_SPACING.md,
            borderRadius: EMZI_RADIUS.lg,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            zIndex: 5,
            minWidth: '180px',
          }}
        >
          <div style={{ fontSize: '12px', fontWeight: '700', marginBottom: EMZI_SPACING.sm, color: EMZI_COLORS.textPrimary }}>
            Legend
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px' }}>
              <div style={{ width: '12px', height: '12px', borderRadius: '2px', backgroundColor: EMZI_COLORS.warning }} />
              <span>Campaign</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px' }}>
              <div style={{ width: '12px', height: '12px', borderRadius: '2px', backgroundColor: EMZI_COLORS.primary }} />
              <span>Product/Service</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px' }}>
              <div style={{ width: '12px', height: '12px', borderRadius: '2px', backgroundColor: EMZI_COLORS.secondary }} />
              <span>Team</span>
            </div>
            <div style={{ fontSize: '10px', color: EMZI_COLORS.textMuted, marginTop: '4px', fontStyle: 'italic' }}>
              Click pack chips to view details
            </div>
          </div>
        </div>

        {/* Node Details Panel */}
        {selectedNode && (
          <div
            style={{
              position: 'absolute',
              bottom: EMZI_SPACING.md,
              left: EMZI_SPACING.md,
              backgroundColor: '#FFFFFF',
              padding: EMZI_SPACING.md,
              borderRadius: EMZI_RADIUS.lg,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              zIndex: 5,
              maxWidth: '320px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: EMZI_SPACING.sm }}>
              <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: EMZI_COLORS.textPrimary }}>
                Selected: {selectedNode.data.name}
              </h4>
              <button
                onClick={() => setSelectedNode(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '18px',
                  cursor: 'pointer',
                  color: EMZI_COLORS.textSecondary,
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
            <div style={{ fontSize: '11px', color: EMZI_COLORS.textSecondary }}>
              <strong>Type:</strong> {selectedNode.type}
            </div>
            {selectedNode.data.linkedPacks && (
              <div style={{ marginTop: EMZI_SPACING.sm }}>
                <div style={{ fontSize: '11px', fontWeight: '600', marginBottom: '4px' }}>
                  {selectedNode.data.linkedPacks.length} Linked Packs
                </div>
                <div style={{ fontSize: '10px', color: EMZI_COLORS.textMuted }}>
                  Click pack chips to navigate
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignGraphView;
